import React, { useState, useRef, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import useOnClickOutside from 'use-onclickoutside';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { CountryFilter } from './CountryFilter';
import { usePopupPosition } from '../../hooks';
import { countries } from '../../pages/conversations/countries';

export type CountryFilterMode = 'INCLUDE' | 'EXCLUDE';

export type FilterValueProps = {
  selected: string[];
  mode?: CountryFilterMode;
};

type CountryFilterDropdownProps = {
  relatedCountries?: string[] | OptionType[];
  value?: FilterValueProps;
  onChange?: ({ selected, mode }: FilterValueProps) => void;
  short?: boolean;
  excludeOnly?: boolean;
  label?: string;
  buttonClassName?: string;
  badgeClassName?: string;
};

export const CountryFilterDropdown: React.FC<CountryFilterDropdownProps> = ({
  relatedCountries,
  value,
  onChange,
  short = false,
  excludeOnly = false,
  label,
  buttonClassName = 'px-2 py-3',
  badgeClassName = 'w-9 h-9',
}) => {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const refAnchor = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { anchorRef } = usePopupPosition();

  const handleCancel = () => setOpen(false);
  const handleApply = () => setOpen(false);

  useOnClickOutside(refAnchor as React.RefObject<HTMLElement>, handleCancel);

  const countryOptions = useMemo(() => {
    if (!relatedCountries?.length) return countries;

    const labelSet = new Set(
      relatedCountries.map((item: any) => {
        if (typeof item === 'string') {
          const found = countries.find(
            c => c.label === item || c.value === item
          );

          return found?.label || item;
        }
        return item.label;
      })
    );

    const matchedCountries = countries.filter(c => labelSet.has(c.label));
    const unmatchedApiCountries = Array.from(labelSet).filter(
      label => !countries.some(c => c.label === label)
    );

    const unmatchedOptions = unmatchedApiCountries.map(label => ({
      value: label,
      label: label,
    }));

    return [...matchedCountries, ...unmatchedOptions];
  }, [relatedCountries]);

  const selectedLabels = useMemo(() => {
    if (!value?.selected?.length) return [];
    return value.selected
      .map(val => {
        const found = countries.find(c => c.label === val || c.value === val);

        return found?.label || val;
      })
      .filter(Boolean) as string[];
  }, [value?.selected]);

  const handleChange = ({ selected, mode }: FilterValueProps) => {
    const mapped = short
      ? selected
          .map(label => countries.find(c => c.label === label)?.value)
          .filter(Boolean)
      : selected;

    onChange?.({ selected: mapped as string[], mode });
  };

  useEffect(() => {
    if (!open || !popupRef.current || !refAnchor.current) return;

    const updatePosition = () => {
      const anchorRect = refAnchor.current?.getBoundingClientRect();
      const popupRect = popupRef.current?.getBoundingClientRect();

      if (!anchorRect || !popupRect) return;

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      const shouldPositionAbove =
        spaceBelow < popupRect.height && spaceAbove > spaceBelow;

      const maxHeight = shouldPositionAbove ? spaceAbove - 20 : spaceBelow - 20;

      let left = anchorRect.left;
      const maxWidth = Math.min(400, viewportWidth - 12);

      if (left + maxWidth > viewportWidth) {
        left = Math.max(16, viewportWidth - maxWidth);
      }

      setPopupStyle({
        position: 'fixed',
        left: `${left}px`,
        top: shouldPositionAbove
          ? `${anchorRect.top - popupRect.height}px`
          : `${anchorRect.bottom + 10}px`,
        maxHeight: `${maxHeight}px`,
        width: `${maxWidth}px`,
        zIndex: 50,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [open]);

  return (
    <div
      ref={node => {
        node && anchorRef(node);
        refAnchor.current = node;
      }}
      className="relative inline-flex items-center gap-2"
    >
      <div className="flex gap-2 items-center">
        {!excludeOnly && (
          <button
            onClick={() =>
              onChange?.({
                selected: [],
                mode: value?.mode || 'INCLUDE',
              })
            }
            className={clsx(
              'flex items-center gap-1 px-3 py-4 border rounded-full text-tiny sm:text-small xs:text-tiny font-medium transition-colors',
              value?.mode === 'INCLUDE'
                ? 'border-success text-green-600 bg-green-50'
                : 'border-danger text-red-600 bg-red-50'
            )}
          >
            {value?.mode === 'INCLUDE' ? <CheckCircleIcon /> : <CancelIcon />}
            <span>{value?.mode === 'INCLUDE' ? 'Include' : 'Exclude'}</span>
          </button>
        )}

        <button
          onClick={() => setOpen(prev => !prev)}
          className={clsx(
            'inline-flex items-center gap-2 border border-main rounded-xl text-small xs:text-tiny font-medium bg-select hover:bg-gray-50 transition-colors',
            buttonClassName
          )}
        >
          <FilterListIcon fontSize="large" />
          <span className="whitespace-nowrap text-tiny sm:text-small">
            {label || (excludeOnly ? 'Exclude Countries' : 'Country Filter')}
          </span>
          <span
            className={clsx(
              'bg-main text-white rounded-full inline-flex items-center justify-center font-medium',
              badgeClassName
            )}
            style={{ lineHeight: 1 }}
          >
            {value?.selected?.length || 0}
          </span>
        </button>
      </div>

      {open && (
        <div
          ref={popupRef}
          style={popupStyle}
          className="fixed z-popper w-fit transition-non-position max-w-[40rem] py-sm bg-white rounded-xl shadow-lg"
        >
          <CountryFilter
            options={countryOptions.map(c => String(c.label))}
            selected={selectedLabels}
            mode={excludeOnly ? 'EXCLUDE' : value?.mode || 'INCLUDE'}
            onChange={handleChange}
            onCancel={handleCancel}
            onApply={handleApply}
            excludeOnly={excludeOnly}
          />
        </div>
      )}
    </div>
  );
};
